import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";


import config from "../../../../config";
import { useRegionView } from "../../../../services/useRegionView";

import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";

const CompareDashboard = () => {
  const API_TOKEN = config.MAP_MATCHING_API_TOKEN;
  const API_URL = config.MAP_MATCHING_API_URL;
  const { island_id } = useRegionView();

  const currentYear = new Date().getFullYear();

  const yearOptions = useMemo(() => {
    const years = [];
    // adjust start year if you want
    for (let y = 2018; y <= currentYear; y += 1) years.push(y);
    return years;
  }, [currentYear]);

  // default: last 2 years (safe)
  const [selectedYears, setSelectedYears] = useState(() => {
    const y1 = currentYear - 1;
    const y2 = currentYear;
    return yearOptions.includes(y1) ? [y1, y2] : [yearOptions.at(-2), yearOptions.at(-1)].filter(Boolean);
  });

  const [loading, setLoading] = useState(false);

  // API returns: { diagrams: [ {labels, datasets:[{label,data,color}]} ... ] }
  const [diagrams, setDiagrams] = useState([]);
  const yearsParam = useMemo(() => {
    // keep stable order + remove duplicates
    const uniqSorted = Array.from(new Set(selectedYears))
      .filter((y) => Number.isFinite(Number(y)))
      .sort((a, b) => Number(a) - Number(b));
    return uniqSorted.join(",");
  }, [selectedYears]);

  const canFetch = selectedYears?.length >= 1 && yearsParam.length > 0;
  useEffect(() => {
     fetchCompareData({ forceIsland100: true });
    
  }, []);

  const fetchCompareData = async ({ forceIsland100 = false } = {}) => {
    if (!canFetch) return;

    setLoading(true);
    try {
      const params = {
        years: yearsParam, // "2020,2021"
      };


      if (forceIsland100) {
        params.island_id = 100;
      } else {
        if (island_id != null && island_id !== 0) {
          params.island_id = island_id;
        }
      }

      const response = await axios.get(`${API_URL}/statistics/compare`, {
        params,
        headers: {
          "X-API-Key": API_TOKEN,
          accept: "*/*",
        },
      });

      const data = response.data;

      if (Array.isArray(data?.diagrams)) {
        setDiagrams(data.diagrams);
      } else {
        setDiagrams([]);
      }
    } catch (e) {
      console.error("Failed to fetch compare statistics", e);
      setDiagrams([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFetch = async () => {
 
    await fetchCompareData();
  };

  // Titles per diagram index (based on your example response meaning)
  const diagramTitles = useMemo(
    () => [
      { title: "Ατυχήματα & Θανατηφόρα", description: "Σύγκριση ανά έτος" },
      { title: "Παθόντες & Νεκροί", description: "Σύγκριση ανά έτος" },
      { title: "Πεζοί", description: "Σύγκριση ανά έτος" },
    ],
    []
  );

  return (
    <DashboardLayout>
      <MDBox mt={4} p={0}>
        <Grid container spacing={3}>
          {/* Header + Filters */}
          <Grid item xs={12}>
            <Card sx={{ overflow: "visible" }}>
              <MDBox p={3}>
                <MDTypography
                  variant="h5"
                  sx={{ lineHeight: 1.3, maxWidth: "900px" }}
                >
                  Σύγκριση Δεικτών ανά έτος  
                </MDTypography>
  

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <MDTypography
                    component="label"
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    sx={{ mb: 1, mr: 1 }}
                  >
                    Έτη (πολλαπλή επιλογή)
                  </MDTypography>

                  <Autocomplete
                    multiple
                    options={yearOptions}
                    value={selectedYears}
                    onChange={(_, newValue) => setSelectedYears(newValue)}
                    disableCloseOnSelect
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Επίλεξε 2+ έτη"
                      />
                    )}
                  />

                  <MDBox mt={1}>
                    <MDTypography variant="button" color="text" sx={{ opacity: 0.8 }}>
                     Επιλεγμένα: {yearsParam || "—"}
                    </MDTypography>
                  </MDBox>
                </Grid>

                  {/* Update button */}
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "flex-start", md: "flex-end" },
                      alignItems: "flex-end",
                    }}
                  >
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={handleFetch}
                      disabled={loading || !canFetch}
                      sx={{ "&.MuiButton-root": { textTransform: "none" } }}
                    >
                      {loading ? "Φόρτωση..." : "Ενημέρωση Στατιστικών"}
                    </MDButton>
                  </Grid>

                  {/* Tip */}
                  <Grid item xs={12}>
                    <MDBox
                      sx={{
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: "rgba(0,0,0,0.02)",
                      }}
                    >
                      <MDTypography variant="button" color="text" sx={{ opacity: 0.85 }}>
                        Tip: Επίλεξε 2+ έτη για πιο χρήσιμη σύγκριση.
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          {/* 3 Bar charts */}
          <Grid item xs={12}>
            <Card sx={{ overflow: "visible" }}>
              <MDBox p={3}>
                <Grid container spacing={3}>
                  {diagrams.map((chart, idx) => (
                    <Grid key={idx} item xs={12} md={6}>
                      <MDBox px={{ xs: 1, md: 2 }}>
                        <VerticalBarChart
                          icon={{ color: "dark", component: "leaderboard" }}
                          title={diagramTitles[idx]?.title || `Διάγραμμα ${idx + 1}`}
                          description={diagramTitles[idx]?.description || ""}
                          chart={chart}
                        />
                      </MDBox>
                    </Grid>
                  ))}

                  {/* Fallback if API returns nothing */}
                  {!loading && diagrams.length === 0 && (
                    <Grid item xs={12}>
                      <MDBox px={{ xs: 1, md: 2 }}>
                        <MDTypography variant="button" color="text" sx={{ opacity: 0.8 }}>
                          Δεν υπάρχουν διαθέσιμα διαγράμματα για τη συγκεκριμένη επιλογή.
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default CompareDashboard;